import * as React from 'react';
import { Component } from 'react';
import { action, computed } from 'mobx';
import { observer, Provider } from 'mobx-react';
import { inject, sortValues } from '../utils';
import get from 'lodash/get';
import FundCard from './FundCard';
import FundDocuments from './FundDocuments';
import getDataPoint from '../utils/getDataPoint';
import {
    DataStore,
    TranslationStore,
    FilterStore,
    EntityStore,
    EntityGroupStore,
} from '../stores';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';
import { camelCase } from 'change-case';
import Loading from './Loading';
import { Stores } from '../models/Stores';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { DataSort } from '../models/configuration/DataSort';
import { ShareClass, ShareClassDocument } from '../models/entity';
import { SortDirections } from '../types';

interface ShareClassProps {
    // Injected by mobx
    dataStore?: DataStore;
    translationStore?: TranslationStore;
    filterStore?: FilterStore;
    entityGroupStore?: EntityGroupStore;
    // Passed Props
    shareClass: ShareClass;
    columns?: Array<ColumnConfig>;
    rowIndex: number;
    groupIndex: number;
    [key: string]: any;
}

// Higher-order component to return a shareclass row/card with data and methods required to function
const withShareclass = WrappedComponent => {
    @inject('dataStore', 'translationStore', 'filterStore', 'entityGroupStore')
    @observer
    class Shareclass extends Component<ShareClassProps, any> {
        entityStore: EntityStore;

        constructor(props: ShareClassProps) {
            super(props);
            const stores: Stores = {
                data: props.dataStore,
                filter: props.filterStore,
                entityGroup: props.entityGroupStore,
            };
            this.entityStore = new EntityStore(
                stores,
                props.shareClass,
                props.rowIndex,
                props.groupIndex
            );
        }

        @computed
        get dataStore(): DataStore {
            return this.props.dataStore;
        }

        async componentDidMount() {
            const { dataStore } = this.props;
            // fetch initial fund card details
            if (this.entityStore.expanded || dataStore.isExpanded) {
                this.entityStore.initialValue = this.props.shareClass;
                await this.entityStore.getShareClassDetail();
            }
        }

        async componentDidUpdate() {
            // fetch fund card details when tabs are changed
            this.entityStore.initialValue = this.props.shareClass;
            await this.entityStore.getShareClassDetail();
        }

        @computed
        get noDataPlaceholder() {
            return this.props.dataStore.config.noDataPlaceholder;
        }

        /**
         * Returns a string of ids that can be used with aria as the row context
         * example: aria-describedby="KS0453474867-column-0 KS0453474867-column-1"
         */
        @computed
        get rowAccessibleContext() {
            const columns = this.columns.filter(
                column => column.isRowIdentifier
            );

            if (columns?.length > 0) {
                let value = columns[0].elementId;
                for (let index = 1; index < columns.length; index++) {
                    const column = columns[index];
                    if (column.elementId) {
                        value += ` ${column.elementId}`;
                    }
                }
                return value;
            }
        }

        @computed
        get columns() {
            const { dataStore } = this.props;
            const { culture: locale } = dataStore;
            const { initialValue: shareClass } = this.entityStore;
            const values = this.props.columns.map(
                (columnConfig, index: number) => {
                    const {
                        type,
                        property,
                        data,
                        columns,
                        groups,
                        disabledEntityCheck,
                    } = columnConfig;

                    const column = {
                        ...columnConfig,
                        elementId: `${shareClass.clientCode}-column-${index}`,
                    };

                    const emptyResponse = col => ({
                        ...col,
                        data: {
                            value:
                                col.noDataPlaceholder || this.noDataPlaceholder,
                        },
                    });

                    // GROUPED COLUMNS
                    if (type === 'group' && columns.length > 0) {
                        return {
                            ...column,
                            values: columns.map(c => {
                                if (
                                    c.disabledEntityCheck &&
                                    this.entityStore.isEntityDisabled
                                ) {
                                    return emptyResponse(c);
                                }

                                if (isArrayLike(c.data)) {
                                    const columnData = c.data.map(d => ({
                                        ...d,
                                        value: getDataPoint({
                                            dataStore,
                                            shareClass,
                                            dataConfig: d,
                                            locale,
                                            fundCurrency: this.fundCurrency,
                                            defaultNoDataPlaceholder:
                                                c.noDataPlaceholder ||
                                                this.noDataPlaceholder,
                                            hasPerformance: this.entityStore
                                                .hasPerformance,
                                        }),
                                    }));
                                    return {
                                        ...c,
                                        multiple: true,
                                        data: columnData,
                                    };
                                }

                                if (c.data) {
                                    return {
                                        ...c,
                                        data: {
                                            ...c.data,
                                            value: getDataPoint({
                                                dataStore,
                                                shareClass,
                                                dataConfig: c.data,
                                                locale,
                                                fundCurrency: this.fundCurrency,
                                                defaultNoDataPlaceholder:
                                                    c.noDataPlaceholder ||
                                                    this.noDataPlaceholder,
                                                hasPerformance: this.entityStore
                                                    .hasPerformance,
                                            }),
                                        },
                                    };
                                }
                            }),
                        };
                    }

                    if (type === 'dropdown' && groups.length > 0) {
                        return {
                            ...column,
                            groups: groups.map(g => ({
                                ...g,
                                values: g.columns.map(c => {
                                    if (
                                        c.disabledEntityCheck &&
                                        this.entityStore.isEntityDisabled
                                    ) {
                                        return emptyResponse(c);
                                    }

                                    if (isArrayLike(c.data)) {
                                        return {
                                            ...c,
                                            multiple: true,
                                            data: c.data.map(d => ({
                                                ...d,
                                                value: getDataPoint({
                                                    dataStore,
                                                    shareClass,
                                                    dataConfig: d,
                                                    locale,
                                                    fundCurrency: this
                                                        .fundCurrency,
                                                    defaultNoDataPlaceholder:
                                                        c.noDataPlaceholder ||
                                                        this.noDataPlaceholder,
                                                    hasPerformance: this
                                                        .entityStore
                                                        .hasPerformance,
                                                }),
                                            })),
                                        };
                                    }

                                    if (c.data) {
                                        return {
                                            ...c,
                                            data: {
                                                ...c.data,
                                                value: getDataPoint({
                                                    dataStore,
                                                    shareClass,
                                                    dataConfig: c.data,
                                                    locale,
                                                    fundCurrency: this
                                                        .fundCurrency,
                                                    defaultNoDataPlaceholder:
                                                        c.noDataPlaceholder ||
                                                        this.noDataPlaceholder,
                                                    hasPerformance: this
                                                        .entityStore
                                                        .hasPerformance,
                                                }),
                                            },
                                        };
                                    }
                                }),
                            })),
                        };
                    }

                    // NON-GROUPED COLUMNS
                    if (
                        disabledEntityCheck &&
                        this.entityStore.isEntityDisabled
                    ) {
                        return emptyResponse(column);
                    }

                    if (type === 'document') {
                        return {
                            ...column,
                            value: this.getDocuments(column),
                        };
                    }

                    if (isArrayLike(data)) {
                        let columnValue = '';
                        const columnData = data.map(d => {
                            const value = getDataPoint({
                                dataStore,
                                shareClass,
                                dataConfig: d,
                                locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder:
                                    column.noDataPlaceholder ||
                                    this.noDataPlaceholder,
                                hasPerformance: this.entityStore.hasPerformance,
                            });
                            columnValue += value + (d.separator || '');
                            return {
                                ...d,
                                value,
                            };
                        });
                        this.orchestrateGtmRowDataContext(
                            column.label,
                            columnValue
                        );
                        return {
                            ...column,
                            multiple: true,
                            data: columnData,
                        };
                    }

                    if (data) {
                        const value = getDataPoint({
                            dataStore,
                            shareClass,
                            dataConfig: data,
                            locale,
                            fundCurrency: this.fundCurrency,
                            defaultNoDataPlaceholder:
                                column.noDataPlaceholder ||
                                this.noDataPlaceholder,
                            hasPerformance: this.entityStore.hasPerformance,
                        });
                        this.orchestrateGtmRowDataContext(column.label, value);
                        return {
                            ...column,
                            data: {
                                ...data,
                                value,
                            },
                        };
                    }

                    // Support simple label/property column definition
                    if (property) {
                        const value = getDataPoint({
                            dataStore,
                            shareClass,
                            dataConfig: {
                                property: 'properties_pub',
                                code: property,
                                key: 'value',
                                performanceCheck: column.performanceCheck,
                            },
                            locale,
                            fundCurrency: this.fundCurrency,
                            defaultNoDataPlaceholder:
                                column.noDataPlaceholder ||
                                this.noDataPlaceholder,
                            hasPerformance: this.entityStore.hasPerformance,
                        });
                        this.orchestrateGtmRowDataContext(column.label, value);
                        return {
                            ...column,
                            data: {
                                ...data,
                                value,
                            },
                        };
                    }

                    return emptyResponse(column);
                }
            );

            return values;
        }

        getDocuments = (column: ColumnConfig) => {
            const {
                documentType,
                performanceCheck,
                noDataPlaceholder = this.noDataPlaceholder,
                index,
                sort,
            } = column;

            if (performanceCheck && !this.entityStore.hasPerformance) {
                return noDataPlaceholder;
            }

            let result = [];

            // get initial list of documents by document type
            const { documents = [] } =
                this.entityStore.groupedDocuments.find(
                    d => d.documentGroupName === documentType
                ) || {};

            result = documents;
            const documentCount = result.length;

            if (documentCount > 0) {
                if (sort) {
                    const dataSort: DataSort = {
                        direction: (sort.direction as SortDirections) || 'ASC',
                        type:
                            (sort.type as 'number' | 'string' | 'date') ||
                            'string',
                        key: sort.key,
                    };
                    result = sortValues(result, dataSort);
                }

                if (index !== undefined) {
                    let document: ShareClassDocument;
                    // When index is negative, index the array in reverse order
                    document =
                        index >= 0
                            ? get(
                                  result,
                                  `[${index}]`,
                                  // If the index is out of bounds, return the last document
                                  result[documentCount - 1]
                              )
                            : get(
                                  result,
                                  `[${documentCount - -index}]`,
                                  // If the index is out of bounds in the reverse index order, return the first document
                                  result[0]
                              );

                    result = [document];
                }
            }

            return result;
        };

        @action
        orchestrateGtmRowDataContext = (label: string, value: any) => {
            if (label) {
                const camelLabel = camelCase(label);
                this.entityStore.gtmRowDataContext[camelLabel] = value;
            }
        };

        @computed
        get fundCurrency() {
            return get(
                this.entityStore.value,
                `properties_pub.${this.props.dataStore.config
                    .fundCurrencyProperty || 'ccy'}.value`
            );
        }

        render() {
            const { entityStore, columns, props } = this;
            const { entityGroupStore } = props;

            let component = null;

            if (entityStore.expanded) {
                if (entityStore.shareClassDetailLoading) {
                    component = <Loading />;
                } else if (entityGroupStore.rowType === 'FundCard') {
                    component = (
                        <FundCard describedBy={this.rowAccessibleContext} />
                    );
                } else if (entityGroupStore.rowType === 'Documents') {
                    component = (
                        <FundDocuments
                            describedBy={this.rowAccessibleContext}
                        />
                    );
                }
            }

            return (
                <Provider entityStore={entityStore}>
                    <WrappedComponent
                        {...this.props}
                        columns={columns}
                        rowAccessibleContext={this.rowAccessibleContext}
                    >
                        {component}
                    </WrappedComponent>
                </Provider>
            );
        }
    }

    return Shareclass;
};

export default withShareclass;



// WEBPACK FOOTER //
// ./src/Components/withShareclass.tsx